// THE BROWSER'S HALF OF APP HEALTH → OPENS: which navigations are opens, how a
// page's paints are seen, and what one open reports. components/open-beacon.tsx
// wires it to the page; lib/open-timing.mjs says when an open felt loaded and
// when it settled (the harness's definitions, imported, never copied).
//
// ─── Which navigations are opens ────────────────────────────────────────────
//
// An open is one page shown to a person. It begins at:
//   - a DOCUMENT LOAD (`hard`): a typed address, a bookmark, a reload, a link
//     from outside. Timed from the navigation's start;
//   - a CLIENT NAVIGATION A PERSON CAUSED (`soft`): the first change of the
//     address on screen (path or query) after a click, or a key pressed
//     outside a text field (a link, a pill, a row, a button that navigates).
//     Timed from that click or key;
//   - BACK OR FORWARD (`soft`): the address change after a `popstate`, timed
//     from it.
// And never at: a `router.refresh()` after a write (the address does not
// change); the replace that stamps `?as=` on an address that had none, or any
// other change the app makes on its own (no click since the last open: ONE
// CLICK MAKES AT MOST ONE OPEN, and whatever the address does after it is part
// of that open); a search box's replace as someone types (a key in a text
// field is typing, and cancels the click that focused it); a click that
// changes nothing.
//
// ─── How the paints are seen, cheaply ───────────────────────────────────────
//
// A DOCUMENT LOAD reads the browser's own `largest-contentful-paint` entries
// (buffered from the navigation's start), and its `layout-shift` entries: no
// work of ours while it paints.
//
// A CLIENT NAVIGATION has no LCP (Chrome reports it for the document only), so
// it reads Element Timing, which reports an element's first paint only if the
// element carries an `elementtiming` attribute when it paints. So the beacon
// tags what the open inserted, and only that, only while an open is being
// timed: once when the address changes (the new page is in the DOM by then,
// not yet painted), then each later insertion under `<main>` from a
// MutationObserver, until the open is over and the observer is gone.
//
// ⚠️ THE ATTRIBUTE GOES ON THE BLOCK THAT OWNS THE TEXT, NOT ON THE TEXT'S
// PARENT. Chromium times a text's paint against its nearest block container:
// `<td><span>cell</span></td>` is reported when `td` carries the attribute,
// and NOT when the `span` does (seen in Playwright's Chromium, 2026-11-05). An
// element tagged after it painted is never reported, nor is a change of an
// element's text in place. So each text's parent is tagged, and each ancestor
// above it while that ancestor is an inline element, up to the first that is
// not (a span made a block by its CSS is the parent, so it is tagged too).
//
// What that costs, on the main thread, per open: a walk of `<main>`'s text
// nodes once (an attribute check on each one's parent, and a write on each new
// one), then the same over each later insertion; at most MAX_TAGS writes an
// open. No layout is read and no style. The observers' callbacks keep only
// LCP's candidates (each paint larger than all before it) and every paint in a
// `[data-primary]` element, which lib/open-timing.mjs says gives the same
// answer as every paint. One timer per open, one beacon at its end.
//
// No server import. The pure parts here are unit-tested; the wiring is in the
// component, and an e2e spec drives it in a real browser.

import { feelsLoadedAt, settledAt, SETTLED_COLLECT_MAX_MS, SETTLED_STILL_MS, type Paint, type Shift } from "@/lib/open-timing.mjs";
import type { OpenBeacon, OpenKind } from "@/lib/ops-opens";

/** Where a page sends its opens (app/api/page-open/route.ts). */
export const OPEN_ENDPOINT = "/api/page-open";

/** How long a click on a link waits for its page: a slow page's address changes only when its answer arrives. */
export const OPEN_LINK_WAIT_MS = 30_000;
/** How long any other click (or key) waits: a button that saves, then navigates. */
export const OPEN_INPUT_WAIT_MS = 10_000;
/**
 * The most elements one open tags. Past it the open goes on, reading what it
 * tagged. Tags go in document order, so the cap falls below the first screen.
 * On the rig over a prod snapshot (Client Inbox 4,600 elements, Action Items
 * 5,300) it took the beacon from 12–16 ms an open at 4,000 tags to 4–6 ms,
 * with feels loaded unchanged within the runs' noise.
 */
export const MAX_TAGS = 1_500;

/** What may begin an open: a click on a link, another click or key, or Back / Forward. */
export type OpenCause = "link" | "input" | "traverse";

/**
 * Which address changes are opens (the rule above), as a small machine the
 * component feeds: the person's clicks and keys, `popstate`, and every change
 * of the address on screen. `at` is ms on the page's clock (an event's
 * `timeStamp`, `performance.now()`).
 */
export function createOpenGate(first: string) {
  let address = first;
  let pending: { at: number; until: number } | null = null;
  const wait = (cause: OpenCause) => (cause === "input" ? OPEN_INPUT_WAIT_MS : OPEN_LINK_WAIT_MS);
  return {
    /** A click, a key outside a text field, or a `popstate`: the latest one is what an address change is timed from. */
    cause(cause: OpenCause, at: number): void {
      pending = { at, until: at + wait(cause) };
    },
    /** A key in a text field: typing, so whatever the address does next is not this click's. */
    typed(): void {
      pending = null;
    },
    /** The address on screen is now `next`: the moment the open it begins was caused, or null for none. */
    changed(next: string, now: number): number | null {
      if (next === address) return null;
      address = next;
      const p = pending;
      pending = null;
      return p !== null && now <= p.until ? p.at : null;
    },
  };
}

/** Elements whose box is inline unless their CSS says otherwise: a text inside one is timed against a block above it. */
const INLINE = new Set([
  "A", "ABBR", "B", "BDI", "BDO", "CITE", "CODE", "DATA", "DFN", "EM", "I", "KBD", "LABEL", "MARK", "Q", "S", "SAMP", "SMALL", "SPAN", "STRONG", "SUB", "SUP", "TIME", "U", "VAR",
]);

/**
 * Tag what `root` holds for Element Timing: each non-blank text's parent and
 * the inline ancestors above it up to the first block (see above), and each
 * image. Skips what is tagged already (and stops climbing there). How many
 * elements it tagged; never more than `budget`.
 */
export function tagForTiming(root: Node, budget: number): number {
  let tagged = 0;
  const tag = (el: Element): boolean => {
    if (el.hasAttribute("elementtiming")) return false;
    el.setAttribute("elementtiming", "");
    tagged += 1;
    return true;
  };
  const doc = root.ownerDocument ?? (root as Document);
  const walker = doc.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  if (root.nodeType === 3) {
    climb(root);
  }
  let t: Node | null;
  while (tagged < budget && (t = walker.nextNode())) climb(t);
  if (root.nodeType === 1 || root.nodeType === 9 || root.nodeType === 11) {
    const imgs = (root as Element).getElementsByTagName("img");
    for (let i = 0; i < imgs.length && tagged < budget; i++) tag(imgs[i]);
  }
  return tagged;

  function climb(text: Node): void {
    const v = text.nodeValue;
    if (!v || !/\S/.test(v)) return;
    let el = text.parentElement;
    while (el && tagged < budget) {
      if (!tag(el)) return; // tagged before, and so was the block above it
      if (!INLINE.has(el.tagName)) return;
      el = el.parentElement;
    }
  }
}

/** What an open saw, in ms from what began it (`t0`). */
export interface OpenSeen {
  kind: OpenKind;
  path: string;
  /** Paints: LCP's candidates and every primary one (see above). */
  paints: Paint[];
  shifts: Shift[];
  /** The browser reports layout shifts at all (Chromium). */
  sees: boolean;
  /** The frame the page changed in (a click's) or first painted (a load's), when no block's paint was seen. */
  frame: number;
}

/** The beacon for an open: feels loaded and settled (lib/open-timing.mjs), rounded to the ms. */
export function openResult(seen: OpenSeen): OpenBeacon {
  const painted = feelsLoadedAt(seen.paints);
  const feels = Math.max(0, painted ?? seen.frame);
  const r = seen.sees ? settledAt(feels, seen.shifts) : { settled: feels, shiftAfter: 0 };
  return {
    path: seen.path,
    kind: seen.kind,
    feels: Math.round(feels),
    settled: Math.round(Math.max(feels, r.settled ?? feels)),
    shift: Math.round(r.shiftAfter * 1e4) / 1e4,
    sees: seen.sees,
    painted: painted !== null,
  };
}

/**
 * When an open stops collecting, on the page's clock: when nothing has painted,
 * shifted or been inserted for SETTLED_STILL_MS, or SETTLED_COLLECT_MAX_MS
 * after what began it, whichever is first; but never before the page has had
 * SETTLED_STILL_MS since its address changed (an open whose address changed
 * after 10 s still waits to see what it painted).
 */
export function collectUntil(t0: number, began: number, lastActivity: number): number {
  return Math.max(Math.min(lastActivity + SETTLED_STILL_MS, t0 + SETTLED_COLLECT_MAX_MS), began + SETTLED_STILL_MS);
}

/** Keep a paint if it is one of LCP's candidates (larger than every paint before it) or a primary one. */
export function keepPaint(kept: Paint[], p: Paint, largest: { area: number }): void {
  if (!(p.area > 0)) return;
  if (p.primary) kept.push(p);
  else if (p.area > largest.area) {
    largest.area = p.area;
    kept.push(p);
  }
}

/** Is a key pressed in `target` typing (a text field, a select, an editable region)? */
export function isTyping(target: EventTarget | null): boolean {
  const el = target as (HTMLElement & { isContentEditable?: boolean }) | null;
  if (!el || typeof el.tagName !== "string") return false;
  return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable === true;
}

/** The link a click is on, when it is an in-app link this tab follows; null otherwise. */
export function inAppLink(e: { target: EventTarget | null; button?: number; metaKey?: boolean; ctrlKey?: boolean; shiftKey?: boolean; altKey?: boolean }, origin: string): HTMLAnchorElement | null {
  if ((e.button ?? 0) !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
  const el = e.target as Element | null;
  const a = el && typeof el.closest === "function" ? (el.closest("a[href]") as HTMLAnchorElement | null) : null;
  if (!a || (a.target && a.target !== "_self") || a.hasAttribute("download")) return null;
  try {
    return new URL(a.href).origin === origin ? a : null;
  } catch {
    return null;
  }
}
